<template>
  <v-dialog v-model="open" maxWidth="600">
    <v-card>
      <v-card-title>Configure transition</v-card-title>
      <v-card-text>
        <div class="flex flex-col gap-4">
          <p>
            Tours will transition on navigation in the below order. Use hooks to manipulate DOM or
            wait for DOM elements before the tour step hides/shows:
          </p>
          <ul class="-mt-2 mb-3 ml-2 list-disc space-y-1 pl-4">
            <li>
              <code class="bg-goal rounded px-1 text-xs">next()</code> will proceed with the tour
              step hide/show
            </li>
            <li>
              Utility functions are provided for convenience:
              <code class="bg-goal rounded px-1 text-xs">utils.waitForElement(cssSelector)</code>,
              <code class="bg-goal rounded px-1 text-xs">utils.waitFor(conditionFn)</code>,
              <code class="bg-goal rounded px-1 text-xs">utils.sleep(ms)</code>
            </li>
          </ul>
          <template v-if="prevPath">
            <div class="mb-4 flex flex-col gap-1">
              <label class="text-sm">
                <span class="mr-1 font-bold">Hide hook:</span> "{{ prevPath.name }}"
              </label>
              <component :is="codeEditor.CodeEditor" v-model="hideJs" v-bind="codeEditor.props" />
              <p class="text-grey-2 p-1 text-xs">
                Optional. On forward or back navigation, this hide hook will run immediately, before
                the prompt hides on
                <code class="bg-goal rounded px-1">next()</code>
              </p>
            </div>
          </template>
          <template v-if="nextPath">
            <div v-if="prevPath" class="mb-4 flex flex-col gap-1">
              <label class="text-sm font-bold">URL Path:</label>
              <RfInput
                v-model="tourUrl"
                placeholder="e.g. /dashboard"
                variant="outlined"
                autoWidth
              />
              <p class="text-grey-2 p-1 text-xs">
                Optional. The user's browser will automatically navigate to this URL path.
              </p>
            </div>
            <div class="mb-4 flex flex-col gap-1">
              <label class="text-sm">
                <span class="mr-1 font-bold">Show hook:</span> "{{ nextPath.name }}"
              </label>
              <component :is="codeEditor.CodeEditor" v-model="showJs" v-bind="codeEditor.props" />
              <p class="text-grey-2 p-1 text-xs">
                Optional.
                <template v-if="prevPath">
                  On forward or back navigation, this show hook will run after the hide hook and
                  browser URL transition,
                </template>
                <template v-else> On trigger, this show hook will run immediately, </template>
                before the next prompt shows on
                <code class="bg-goal rounded px-1">next()</code>
              </p>
            </div>
          </template>
        </div>
      </v-card-text>
      <v-card-actions class="modal-card-actions-div">
        <v-btn
          class="cancel-btn"
          variant="outlined"
          width="100px"
          size="large"
          @click="open = false"
          >Cancel</v-btn
        >
        <v-btn
          size="large"
          variant="flat"
          color="success"
          width="200"
          :loading="loading"
          @click="save"
          >Save</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch } from "vue";
import RfInput from "@/components/inputs/RfInput.vue";
import { useCodeEditor } from "@/utils/composables/useCodeEditor";
import { usePromptStore } from "@/pinia/promptStore";
import { useToastsStore } from "@/pinia/toastsStore";

const props = defineProps({
  prevPath: { type: Object, default: null },
  nextPath: { type: Object, default: null },
  appId: { type: String, required: true },
});
const emit = defineEmits(["saved"]);

const open = defineModel({ type: Boolean, default: false });

const codeEditor = useCodeEditor();
const promptStore = usePromptStore();
const toastsStore = useToastsStore();

const HIDE_JS_PLACEHOLDER = `// Run custom JS when the previous step hides, e.g.
/*
  await utils.waitForElement('div.modal');
  await utils.waitFor(() => !document.querySelector('.modal'));
  await next(); // hides previous step
  window.removeSomeState();
*/`;
const SHOW_JS_PLACEHOLDER = `// Run custom JS when the next step shows, e.g.
/*
  doSyncWork();
  setTimeout(next, 1000); // shows next step
*/`;

const tourUrl = ref("");
const showJs = ref(SHOW_JS_PLACEHOLDER);
const hideJs = ref(HIDE_JS_PLACEHOLDER);
const loading = ref(false);

watch(
  () => [props.prevPath, props.nextPath],
  ([prev, next]) => {
    hideJs.value = prev?.actions?.rf_settings_tour_hide_js || HIDE_JS_PLACEHOLDER;
    tourUrl.value = next?.actions?.rf_settings_tour_url ?? "";
    showJs.value = next?.actions?.rf_settings_tour_show_js || SHOW_JS_PLACEHOLDER;
  },
  { immediate: true },
);

const updatePathActions = (path, actions) =>
  promptStore.updatePrompt({
    appId: props.appId,
    pathId: path.id,
    modelPath: { ...path, actions: { ...path.actions, ...actions } },
  });

const save = async () => {
  loading.value = true;
  try {
    const hideHook = hideJs.value?.trim() === HIDE_JS_PLACEHOLDER ? "" : hideJs.value;
    const showHook = showJs.value?.trim() === SHOW_JS_PLACEHOLDER ? "" : showJs.value;
    const saves = [];
    if (props.prevPath)
      saves.push(updatePathActions(props.prevPath, { rf_settings_tour_hide_js: hideHook }));
    if (props.nextPath)
      saves.push(
        updatePathActions(props.nextPath, {
          rf_settings_tour_url: tourUrl.value,
          rf_settings_tour_show_js: showHook,
        }),
      );
    await Promise.all(saves);
    toastsStore.create({ type: "success", body: "Transition settings saved" });
    open.value = false;
    emit("saved");
  } catch {
    toastsStore.create({ type: "error", body: "Failed to save transition settings" });
  } finally {
    loading.value = false;
  }
};
</script>
